import * as React from "react";
import { v0_9 } from "@a2ui-sdk/react";

const { useStringBinding: useA2UIStringBinding, useDataModel, useScope, useDataBinding } = v0_9;

export { useDataBinding };

// Custom wrapper to handle A2UI custom functions generated by the AI agent
export const useStringBinding = (surfaceId: string, source: any, defaultValue?: string) => {
  const dataModel = useDataModel(surfaceId);
  const { basePath } = useScope();

  // 1. Delegate formatString or fallback completely to the native useA2UIStringBinding
  const isFunctionCall = source && typeof source === "object" && source.call;
  const a2uiString = useA2UIStringBinding(
    surfaceId,
    (isFunctionCall && source.call === "formatString" && source.args?.value)
      ? source.args.value
      : isFunctionCall ? undefined : source,
    defaultValue
  );

  return React.useMemo(() => {
    if (isFunctionCall) {
      const fnName = source.call;
      const args = source.args || {};

      if (fnName === "formatString" && typeof args.value === "string") {
         return a2uiString;
      }

      // Helper to manually resolve args (paths vs literals)
      const resolveArg = (val: any): any => {
        if (val && typeof val === "object" && val.path) {
          let resolvedPath = val.path;
          if (!resolvedPath.startsWith("/") && basePath) {
            resolvedPath = `${basePath}/${resolvedPath}`;
          }
          const parts = resolvedPath.split("/").filter(Boolean);
          let res = dataModel as any;
          for (const p of parts) {
            if (res && typeof res === "object") res = res[p];
            else return undefined;
          }
          return res;
        }
        return val;
      };

      const value = resolveArg(args.value);

      try {
        if (fnName === "formatNumber") {
          const num = Number(value);
          const decimals = args.decimals !== undefined ? Number(resolveArg(args.decimals)) : undefined;
          const grouping = args.grouping !== undefined ? Boolean(resolveArg(args.grouping)) : true;
          if (!isNaN(num)) {
            return new Intl.NumberFormat(undefined, { 
              useGrouping: grouping, 
              minimumFractionDigits: decimals, 
              maximumFractionDigits: decimals 
            }).format(num);
          }
        }
        if (fnName === "formatCurrency") {
          const num = Number(value);
          const currency = String(resolveArg(args.currency) || "EUR");
          const decimals = args.decimals !== undefined ? Number(resolveArg(args.decimals)) : undefined;
          const grouping = args.grouping !== undefined ? Boolean(resolveArg(args.grouping)) : true;
          if (!isNaN(num)) {
            return new Intl.NumberFormat(undefined, { 
              style: "currency", 
              currency, 
              useGrouping: grouping, 
              minimumFractionDigits: decimals, 
              maximumFractionDigits: decimals 
            }).format(num);
          }
        }
        if (fnName === "formatDate") {
          if (value) {
            const date = new Date(value);
            const formatStr = String(resolveArg(args.format) || "");
            // Basic approximation of TR35 format strings for visual rendering
            if (formatStr.includes("MMM")) {
               return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
            }
            if (formatStr.includes("HH:mm") || formatStr.includes("h:mm")) {
               return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
            }
            return date.toLocaleDateString();
          }
        }
        if (fnName === "pluralize") {
          const count = Number(value);
          const other = String(resolveArg(args.other) || "");
          if (count === 0 && args.zero !== undefined) return String(resolveArg(args.zero));
          if (count === 1 && args.one !== undefined) return String(resolveArg(args.one));
          if (count === 2 && args.two !== undefined) return String(resolveArg(args.two));
          return other;
        }
      } catch (e) {
        console.warn(`[A2UI] Failed to evaluate custom function ${fnName}:`, e);
      }

      return String(value ?? defaultValue ?? "");
    }

    return a2uiString;
  }, [source, dataModel, basePath, a2uiString, defaultValue, isFunctionCall]);
};
